import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/auth";

const TYPE_FILTERS = ["all", "volunteer", "buddy", "organization"];
const STATUS_FILTERS = ["all", "SUBMITTED", "IN_REVIEW", "NEEDS_INFORMATION", "APPROVED", "DECLINED", "ACTIVE", "PROFILE_SETUP", "OFFERINGS_REVIEW"];

export default function OnboardingQueue() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = {};
    if (type !== "all") params.type = type;
    if (status !== "all") params.status = status;
    api.get("/ops/applications", { params }).then((r) => setItems(r.data.items || []))
      .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, status]);

  const grouped = useMemo(() => items, [items]);

  return (
    <div className="space-y-6" data-testid="ops-onboarding-queue">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-semibold text-slate-900">Onboarding queue</h1>
        <div className="flex gap-2 flex-wrap">
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="ops-queue-type-filter" className="text-sm border rounded px-2 py-1 bg-white">
            {TYPE_FILTERS.map((t) => <option key={t} value={t}>Type: {t}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ops-queue-status-filter" className="text-sm border rounded px-2 py-1 bg-white">
            {STATUS_FILTERS.map((s) => <option key={s} value={s}>Status: {s}</option>)}
          </select>
        </div>
      </div>
      {err && <div className="text-sm text-red-700">{err}</div>}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Applicant</th>
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Submitted</th>
              <th className="text-left px-4 py-2">Last activity</th>
              <th className="text-right px-4 py-2">Open</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>}
            {!loading && grouped.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No applications match the current filter.</td></tr>}
            {grouped.map((r) => (
              <tr key={`${r.type}:${r.id}`} className="border-t hover:bg-slate-50" data-testid="ops-queue-row">
                <td className="px-4 py-3 text-slate-900">
                  <div className="font-medium">{r.applicant_name || r.organization_name || "—"}</div>
                  <div className="text-xs text-slate-500">{r.applicant_email}{r.location ? ` · ${r.location}` : ""}</div>
                </td>
                <td className="px-4 py-3 capitalize">{r.type}</td>
                <td className="px-4 py-3"><StatusBadge s={r.status} /></td>
                <td className="px-4 py-3 text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-slate-500">{(r.last_status_change_at || r.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3 text-right">
                  {r.type === "buddy" ? (
                    <Link to="/portal/admin/buddies" className="text-sm font-medium text-slate-900" data-testid={`ops-queue-open-buddy-${r.id}`}>Buddy admin →</Link>
                  ) : (
                    <Link to={`/ops/onboarding/${r.id}`} className="text-sm font-medium text-slate-900" data-testid={`ops-queue-open-${r.id}`}>Open →</Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ s }) {
  const map = {
    SUBMITTED: "bg-blue-100 text-blue-800",
    IN_REVIEW: "bg-amber-100 text-amber-800",
    NEEDS_INFORMATION: "bg-orange-100 text-orange-800",
    APPROVED: "bg-emerald-100 text-emerald-800",
    ACTIVE: "bg-emerald-100 text-emerald-800",
    DECLINED: "bg-rose-100 text-rose-800",
    PROFILE_SETUP: "bg-indigo-100 text-indigo-800",
    OFFERINGS_REVIEW: "bg-indigo-100 text-indigo-800",
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[s] || "bg-slate-100 text-slate-700"}`}>{s}</span>;
}
